import { Anchors, registerPluginComponents, registerPluginMetadataApi } from '@kaoto/kaoto';
import { lazy } from 'react';

import {
  always,
  isMappingStepNode,
  isReshapeNode,
  isRouteGroupNode,
  isSplitChoiceNode,
  isVendorContractNode,
  isWrapNode,
} from './activation/activation';
import { registerPlatformComponents } from './host/custom-catalog';
import { CasaMetadataApi } from './host/metadata-api';

/**
 * Supplying the host API is what lets Kaoto's own editors work in the self-hosted app: the
 * DataMapper writes its stylesheet into Casa's control plane instead of nowhere.
 */
registerPluginMetadataApi(new CasaMetadataApi());

/**
 * Our own Camel components, described so their nodes can be configured. Without this the canvas
 * draws them and then refuses to open them, which is worse than not drawing them at all.
 */
registerPlatformComponents();

registerPluginComponents([
  {
    anchor: Anchors.CanvasGroupTitlebar,
    activationFn: isRouteGroupNode,
    component: lazy(() => import('./components/GroupCompletionBadge')),
  },
  {
    anchor: Anchors.CanvasFormHeader,
    activationFn: always,
    component: lazy(() => import('./components/GroupScopeBanner')),
  },
  {
    anchor: Anchors.SettingsSection,
    activationFn: always,
    component: lazy(() => import('./components/RouteGroupsSettings')),
  },
  {
    anchor: Anchors.CanvasGroupTitlebar,
    activationFn: isRouteGroupNode,
    component: lazy(() => import('./components/GroupPublicationBadge')),
  },
  {
    anchor: Anchors.CanvasFormHeader,
    activationFn: always,
    component: lazy(() => import('./components/GroupTestBadge')),
  },
  /**
   * The rules a vendor's payload is held to, on the node that enforces them. It is the node's
   * whole purpose, so it belongs in the node's form rather than on a screen somewhere else.
   */
  {
    anchor: Anchors.CanvasFormHeader,
    activationFn: isVendorContractNode,
    component: lazy(() => import('./components/VendorContractForm')),
  },
  /**
   * What a tenant asked to have done to their documents, on the node that does it. A route
   * without this node is unaffected, which is what lets one drawn flow serve both kinds of tenant.
   */
  /** A split, on the choice it draws, where it is changed. */
  {
    anchor: Anchors.CanvasFormHeader,
    activationFn: isSplitChoiceNode,
    component: lazy(() => import('./components/SplitChoiceForm')),
  },
  /** Everything about a mapping, as one row above the mapper the step's Configure opens. */
  {
    anchor: Anchors.DataMapperHeader,
    activationFn: isMappingStepNode,
    component: lazy(() => import('./components/MappingStepBar')),
  },
  {
    anchor: Anchors.CanvasFormHeader,
    activationFn: isReshapeNode,
    component: lazy(() => import('./components/ReshapeForm')),
  },
  {
    anchor: Anchors.CanvasFormHeader,
    activationFn: isWrapNode,
    component: lazy(() => import('./components/WrapForm')),
  },
  {
    anchor: Anchors.CanvasToolbar,
    activationFn: always,
    component: lazy(() => import('./components/SelectionToolbar')),
  },
  {
    anchor: Anchors.WorkspaceHeader,
    activationFn: always,
    component: lazy(() => import('./components/IntegrationWorkspace')),
  },
  /**
   * The board of boxes for every level above a route. It renders nothing on a route, which is
   * what lets Kaoto's own canvas come back for the real flow.
   */
  {
    anchor: Anchors.WorkspaceHeader,
    activationFn: always,
    component: lazy(() => import('./components/OverviewSurface')),
  },
]);
